import { filterMapper } from '@/core/mappers/filter.mapper';
import { StoreState } from '@/core/models/store.interface';
import { LocationQuery } from 'vue-router';
import { Module } from 'vuex';
import { SavedFilter } from './filter.interface';
import { FilterService } from './filter.service';

const service = new FilterService();

export interface FilterState {
   [key: string]: Array<SavedFilter> | { total: number; per_page: number } | undefined;
}

const FilterStore: Module<FilterState, StoreState> = {
   namespaced: true,
   state: {},
   getters: {},
   mutations: {
      SET_SAVED_FILTERS(state, { tabKey, savedFilters }: { tabKey: string; savedFilters: Array<SavedFilter> }) {
         state[tabKey] = savedFilters;
      },
      SET_SAVED_FILTERS_META(state, { tabKey, meta }: { tabKey: string; meta: { total: number; per_page: number } }) {
         state[`${tabKey}_meta`] = meta;
      },
      ADD_SAVED_FILTER(state, savedFilter: SavedFilter) {
         state[savedFilter.tab_key]?.push(savedFilter);
      },
      SET_SAVED_FILTER(state, savedFilter: SavedFilter) {
         const index = state[savedFilter.tab_key]?.findIndex((sf) => sf.id === savedFilter.id);
         if (index > -1) state[savedFilter.tab_key].splice(index, 1, savedFilter);
      },
      DELETE_SAVED_FILTER(state, savedFilter: SavedFilter) {
         const index = state[savedFilter.tab_key]?.findIndex((sf) => sf.id === savedFilter.id);
         if (index > -1) state[savedFilter.tab_key].splice(index, 1);
      },
   },
   actions: {
      async getSavedFilters(context, { tabKey, targetType, sourceType }: { tabKey: string; targetType: string; sourceType: string }) {
         const response = await context.dispatch('searchSavedFilters', { tabKey, targetType, sourceType });
         context.commit('SET_SAVED_FILTERS', { tabKey, savedFilters: response.data });
         context.commit('SET_SAVED_FILTERS_META', { tabKey, meta: { total: response.meta.total, per_page: response.meta.per_page } });
         return response.data;
      },
      async searchSavedFilters(
         context,
         {
            tabKey,
            targetType,
            sourceType,
            search,
            page,
         }: { tabKey: string; targetType: string; sourceType: string; search?: string; page?: number },
      ) {
         const params: Record<string, string> = {
            'filter[tab_key]': tabKey,
            'filter[target_type]': targetType,
            'filter[source_type]': sourceType,
            'filter[name]': search || '',
            per_page: '25',
            page: String(page || 1),
            include: 'user',
         };

         const response = await service.getFilters(params);

         return response;
      },
      async getSavedFilter(context, filterId: number) {
         const response = await service.getFilter(filterId);
         return { ...response.data, data: filterMapper(response.data.data) };
      },
      async addFilter(context, savedFilter: SavedFilter) {
         const response = await service.postFilter(savedFilter);
         context.commit('ADD_SAVED_FILTER', response.data);
         return response.data;
      },
      async changeFilter(context, savedFilter: SavedFilter) {
         const response = await service.putFilter(savedFilter);
         context.commit('SET_SAVED_FILTER', response.data);
         return response.data;
      },
      async removeFilter(context, savedFilter: SavedFilter) {
         await service.deleteFilter(savedFilter);
         context.commit('DELETE_SAVED_FILTER', savedFilter);
      },
      async restoreFilter(context, savedFilter: SavedFilter) {
         await service.restoreFilter(savedFilter);
      },
      async getAllFilters(context, query: LocationQuery) {
         const response = await service.getAllFilters(query);
         return response;
      },
   },
};

export default FilterStore;
